import {
  Card,
  CheckboxCard,
  Field,
  Flex,
  Grid,
  GridItem,
  Text,
} from "@chakra-ui/react";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import {
  AvailableHarvestEntity,
  GranoEntity,
  sellOrderSchema,
} from "../schema/sell-order.schema";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AppColors } from "@/styles/colors";
import { useNavigate } from "react-router-dom";
import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { TextArea } from "@/components/text-area/TextArea";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { AppRoutes } from "@/constants/routes";
import { useEffect, useMemo, useRef, useState } from "react";
import { isEmpty } from "@/utils/query-string";
import {
  createSellOrderHandler,
  getOptionsHandler,
  loadOptionsHandler,
} from "../schema/handler-functions";
import { SelectComponent } from "@/components/select/Select";
import { useAuth } from "@/hooks/useAuth";
import { getListadoGranos } from "@/services/aux-entities.service";
import { InputField } from "@/components/input-field/InputField";
import { numbersWithCommaDecimal } from "@/utils/reg-expressions";
import { getAvailableHarvest } from "@/services/sell-order.service";
import { ComboboxComponent } from "@/components/select/Combobox";
import { useRoles } from "@/hooks/useRoles";

export const SellOrderCreationPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  useEffect(() => {
    setIsActive(true);
  }, []);
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    register,
    control,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = useForm({
    resolver: zodResolver(sellOrderSchema),
  });

  const [grains, setGrains] = useState<GranoEntity[] | null>([]);
  const [availableHarvest, setAvailableHarvest] = useState<
    AvailableHarvestEntity[] | null
  >([]);

  const [loadingGrains, setLoadingGrains] = useState<boolean>(false);
  const [loadingAvailableHarvest, setLoadingAvailableHarvest] =
    useState<boolean>(false);

  const fetchAvailableHarvest = async () => {
    setLoadingAvailableHarvest(true);
    const response = await getAvailableHarvest({
      cuenta: Number(watchedValues.account?.value),
      grano: Number(watchedValues.grain),
    });
    setAvailableHarvest(response);
    setLoadingAvailableHarvest(false);
  };

  const fetchGrains = async () => {
    setLoadingGrains(true);
    const response = await getListadoGranos();
    setGrains(response);
    setLoadingGrains(false);
  };

  useEffect(() => {
    if (isActive) {
      fetchGrains();
    }
  }, [isActive]);

  const handleCancel = () => {
    navigate(AppRoutes.SELL_ORDERS);
  };

  const { fetchPermissions } = useRoles()

  const [loadingCreateSellOrder, setLoadingCreateSellOrder] =
    useState<boolean>(false);

  const [loadingPersons, setLoadingPersons] = useState<boolean>(false);
  const watchedValues = useWatch({ control });

  const hasNoFilledValues = useMemo(() => {
    const { account, fixation_type, grain, harvest, ton } = watchedValues || {};

    return (
      isEmpty(account) ||
      isEmpty(fixation_type) ||
      isEmpty(grain) ||
      isEmpty(harvest) ||
      isEmpty(ton) ||
      Number(watchedValues?.ton) > Number(watchedValues?.available_ton)
    );
  }, [watchedValues]);

  useEffect(() => {
    if (
      isActive &&
      watchedValues?.account?.value &&
      watchedValues?.grain
    ) {
      fetchAvailableHarvest();
    }
  }, [isActive, watchedValues?.account?.value, watchedValues?.grain]);

  const handleChangeAvailableTon = (selectedCosecha: any) => {
    const selected = availableHarvest?.find(
      (h) => h.cosecha === selectedCosecha?.[0],
    );
    if (selected) {
      setValue("available_ton", selected?.disponible);
    } else {
      setValue("available_ton", "");
    }
  };

  const handleChangeGrain = () => {
    setValue("available_ton", "");
    setValue("ton", "");
    setValue("harvest", [])
  }

  const handleTonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value || "";
    // Normalize dots to commas
    val = val.replaceAll('.', ',');
    // Keep only digits and comma
    val = val.replaceAll(/[^0-9,]/g, '');
    // Ensure single comma and max 3 decimals
    const parts = val.split(',');
    if (parts.length > 1) {
      const intPart = parts[0];
      const decPart = (parts.slice(1).join('')).slice(0, 3);
      val = decPart.length > 0 ? `${intPart},${decPart}` : `${intPart},`;
    } else {
      val = parts[0];
    }
    // Prevent leading comma (",123") -> "0,123"
    if (val.startsWith(',')) {
      val = `0${val}`;
    }
    e.target.value = val;
  }

  useEffect(() => {
    if (!isEmpty(watchedValues?.available_ton)) {
      const tonNum = Number.parseFloat(String(watchedValues?.ton ?? '').replace(',', '.'));
      const availableNum = Number.parseFloat(String(watchedValues?.available_ton ?? '0').toString().replace(',', '.'));
      if (!Number.isNaN(tonNum) && !Number.isNaN(availableNum) && tonNum > availableNum) {
        setError("ton", {
          message: `La cantidad de grano disponible es: ${Number(availableNum).toLocaleString('es-AR')} Tn.`,
        });
      } else {
        clearErrors("ton");
      }
    }
  }, [watchedValues?.ton, watchedValues?.available_ton, errors]);

  const getOptions = async (
    inputValue: string,
  ): Promise<
    {
      label: string;
      value: string;
      tipoCuenta: string;
      idTipoCuenta: number;
    }[]
  > => {
    return getOptionsHandler({
      inputValue,
    });
  };

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      getOptions,
      inputValue,
      setLoadingPersons,
      timeoutRef,
    });
  };

  const handleCreateSellOrder = async () => {
    await createSellOrderHandler({
      navigate,
      setLoadingCreateSellOrder,
      watchedValues,
      user: user?.name ?? "",
      fetchPermissions
    });
    setLoadingCreateSellOrder(true);
  };

  

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Agregar orden de venta"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleCreateSellOrder}
        disableAdd={hasNoFilledValues}
        loading={loadingCreateSellOrder}
      />
      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(4, 3fr)">
            <GridItem paddingX="16px" paddingY="8px">
              <Field.Root invalid={!!errors?.account?.message} gap="12px">
                <Field.Label>
                  {" "}
                  <Text fontSize="16px" fontWeight={600}>
                    Cuenta
                  </Text>
                </Field.Label>

                <AsyncAutocompleteSelect
                  control={control}
                  loadOptions={loadOptions}
                  name="account"
                  placeholder="Buscar..."
                  isLoading={loadingPersons}
                  disabled={loadingCreateSellOrder}
                />

                <Field.ErrorText>
                  {String(errors?.account?.message)}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>
            <GridItem paddingY="8px" paddingX="16px">
              <Field.Root invalid={!!errors?.fixation_type?.message}>
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>
                    Tipo de fijación
                  </Text>
                </Field.Label>
                <SelectComponent
                  fullWidth
                  items={[
                    {
                      label: "Manual",
                      value: "Manual",
                    },
                    {
                      label: "Automática",
                      value: "Automatica",
                    },
                  ]}
                  menuMaxHeight="250px"
                  name="fixation_type"
                  control={control}
                  closeOnSelect
                  clearable
                  size="xs"
                  minHeight="40px"
                  placeholder="Seleccionar tipo de fijación"
                  disabled={loadingCreateSellOrder}
                />
                <Field.ErrorText>
                  {String(errors?.fixation_type?.message)}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>

            <GridItem paddingY="8px" paddingX="16px">
              <Field.Root invalid={!!errors?.grain?.message}>
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>
                    Grano
                  </Text>
                </Field.Label>

                <ComboboxComponent
                  key={grains?.length ? `grain-loaded` : "grain-empty"}
                  items={
                    grains?.map((el) => ({
                      label: el?.nombre,
                      value: el?.id,
                    })) || []
                  }
                  menuMaxHeight="250px"
                  name="grain"
                  control={control}
                  clearable
                  placeholder="Seleccionar grano"
                  loading={loadingCreateSellOrder || loadingGrains}
                  disabled={loadingCreateSellOrder || loadingGrains}
                  onSelect={handleChangeGrain}
                />

                <Field.ErrorText>
                  {String(errors?.grain?.message)}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>

            <GridItem paddingY="8px" paddingX="16px">
              <Field.Root invalid={!!errors?.harvest?.message}>
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>
                    Cosecha
                  </Text>
                </Field.Label>
                <SelectComponent
                  fullWidth
                  items={
                    availableHarvest?.map((el) => ({
                      label: `${el.cosecha.slice(0, 2)}-${el.cosecha.slice(2)}`,
                      value: el.cosecha,
                    })) || []
                  }
                  menuMaxHeight="250px"
                  name="harvest"
                  control={control}
                  handleChange={handleChangeAvailableTon}
                  closeOnSelect
                  clearable
                  size="xs"
                  minHeight="40px"
                  placeholder="Seleccionar cosecha"
                  loading={loadingAvailableHarvest}
                  disabled={
                    loadingCreateSellOrder ||
                    loadingAvailableHarvest ||
                    Boolean(!availableHarvest?.length)
                  }
                />
                <Field.ErrorText>
                  {String(errors?.harvest?.message)}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>

            <GridItem paddingY="8px" paddingX="16px">
              <InputField
                inputName="ton"
                labelText="Toneladas"
                control={control}
                allowedPattern={numbersWithCommaDecimal}
                type="text"
                onChange={handleTonChange}
                disabled={loadingCreateSellOrder}
                placeholder="Ingrese toneladas"
              />
            </GridItem>

            <GridItem paddingX="16px" paddingY="2px">
              <Field.Root invalid={!!errors?.price?.message} gap="12px">
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>
                    Precio
                  </Text>
                </Field.Label>
                <InputCurrency
                  control={control}
                  name="price"
                  height="40px"
                  maxLength={20}
                />

                <Field.ErrorText>
                  {String(errors?.price?.message)}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>

            <GridItem paddingX="16px" paddingY="30px">
              <CheckboxCard.Root maxW="240px">
                <CheckboxCard.HiddenInput
                  value={watchedValues.isExchange ? "on" : "off"}
                  onChange={(e) => setValue("isExchange", e.target.checked)}
                />
                <CheckboxCard.Control paddingX="16px" paddingY="12px">
                  <CheckboxCard.Label>
                    {" "}
                    <Text fontSize="16px" fontWeight={600}>
                      Es canje
                    </Text>
                  </CheckboxCard.Label>
                  <CheckboxCard.Indicator />
                </CheckboxCard.Control>
              </CheckboxCard.Root>
            </GridItem>
          </Grid>
        </Card.Body>
      </Card.Root>
      <Flex flexDir="column" gap="12px">
        <Text fontWeight={700} fontSize="20px">
          Detalle
        </Text>
        <TextArea
          errors={errors}
          inputName="order_detail"
          maxLength={50}
          register={register}
          placeholder="Ingresar una descripción de la solicitud"
          helperText="Máximo 50 caracteres"
        />
      </Flex>
    </Flex>
  );
};
